import { useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  Star,
  Heart,
  Truck,
  Shield,
  RefreshCw,
  Minus,
  Plus,
  ChevronRight,
  Flame,
  Clock,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { REVIEWS_BY_PRODUCT } from "../data/products";
import {
  fetchProductBySlug,
  fetchProducts,
  fetchCategories,
} from "../lib/storefront";
import { formatINR, useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useContent } from "../lib/content";
import ProductCard from "../components/ProductCard";
import SizeGuideDialog from "../components/SizeGuideDialog";
import SafeImg from "../components/SafeImg";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { trackEvent } from "../lib/tracking";

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const { has: inWishlist, toggle: toggleWishlist } = useWishlist();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [categories, setCategories] = useState([]);
  const [notFound, setNotFound] = useState(false);

  const [mainIdx, setMainIdx] = useState(0);
  const [size, setSize] = useState("M");
  const [qty, setQty] = useState(1);
  const [zoom, setZoom] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  // Rotating "viewing now" count — changes every ~10s to feel live, seeded uniquely per product
  const [viewers, setViewers] = useState(() => 8 + Math.floor(Math.random() * 14));
  useEffect(() => {
    const t = setInterval(() => {
      setViewers((v) => {
        const delta = Math.floor(Math.random() * 5) - 2; // -2..+2
        const next = v + delta;
        if (next < 6) return 6 + Math.floor(Math.random() * 4);
        if (next > 28) return 20 + Math.floor(Math.random() * 5);
        return next;
      });
    }, 9000 + Math.floor(Math.random() * 4000));
    return () => clearInterval(t);
  }, []);

  // Live reviews from backend
  const [liveReviews, setLiveReviews] = useState([]);
  const [reviewSummary, setReviewSummary] = useState({ count: 0, average: 0 });
  // Admin-configured allowed sizes (from settings.size_chart)
  const [allowedSizes, setAllowedSizes] = useState(null);
  useEffect(() => {
    if (!product) return;
    const pid = product.slug || product.id;
    import("../lib/api").then(({ default: api }) => {
      api.get("/reviews", { params: { product_id: pid } }).then((r) => setLiveReviews(r.data)).catch(() => setLiveReviews([]));
      api.get("/reviews/summary", { params: { product_id: pid } }).then((r) => setReviewSummary(r.data)).catch(() => {});
    });
  }, [product?.id]);

  // Fetch active size chart once
  useEffect(() => {
    import("../lib/api").then(({ default: api }) => {
      api.get("/storefront/settings").then((r) => {
        const rows = r.data?.size_chart?.rows || [];
        const sizes = rows.map((row) => row.size).filter(Boolean);
        setAllowedSizes(sizes.length ? sizes : null);
      }).catch(() => setAllowedSizes(null));
    });
  }, []);

  // Pincode serviceability
  const [pincode, setPincode] = useState("");
  const [pinResult, setPinResult] = useState(null);
  const [pinLoading, setPinLoading] = useState(false);
  // Fabric & Care content (CMS)
  const fc = useContent().content?.fabric_care || {};
  const fcEnabled = fc.enabled !== false;
  const checkPincode = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(pincode)) {
      setPinResult({ ok: false, serviceable: false, message: "Please enter a valid 6-digit pincode." });
      return;
    }
    setPinLoading(true);
    setPinResult(null);
    try {
      const { default: api } = await import("../lib/api");
      const { data } = await api.post("/shipping/pincode-check", { pincode });
      setPinResult(data);
    } catch (err) {
      setPinResult({ ok: false, serviceable: false, message: "Could not check pincode. Try again." });
    } finally {
      setPinLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setProduct(null);
    setNotFound(false);
    setMainIdx(0);
    fetchProductBySlug(id)
      .then((p) => {
        if (cancelled) return;
        setProduct(p);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      });
    fetchCategories().then((c) => !cancelled && setCategories(c)).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!product) return;
    trackEvent("ViewContent", {
      value: product.price,
      currency: "INR",
      content_ids: [product.id],
      content_name: product.name,
    });
    fetchProducts({ category: product.category })
      .then((list) => setRelated(list.filter((p) => p.id !== product.id).slice(0, 4)))
      .catch(() => setRelated([]));
    // Auto-pick first in-stock size once product loads
    const firstAvail = (product.sizes || []).find((s) => Number(product.stock?.[s] ?? 0) > 0);
    if (firstAvail) setSize(firstAvail);
  }, [product?.id]);

  if (notFound) return <Navigate to="/shop" replace />;
  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-32 text-center font-body text-ink-500" data-testid="product-detail-loading">
        Loading…
      </div>
    );
  }

  const reviews = REVIEWS_BY_PRODUCT[product.id] || REVIEWS_BY_PRODUCT.default;
  const categoryName = categories.find((c) => c.id === product.category)?.name || product.category;

  const lowStockSize = (() => {
    const avail = Object.entries(product.stock || {})
      .map(([s, n]) => ({ size: s, stock: Number(n) }))
      .filter((x) => x.stock > 0 && x.stock <= 5 && (product.sizes || []).includes(x.size));
    if (!avail.length) return null;
    avail.sort((a, b) => a.stock - b.stock);
    return avail[0];
  })();

  const handleAdd = () => {
    addItem(product, { size, quantity: qty });
  };

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePos({ x, y });
  };

  return (
    <div data-testid="product-detail-page" className="pb-24">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8">
        <nav className="flex items-center gap-2 text-xs font-body uppercase tracking-[0.2em] text-ink-500">
          <Link to="/" className="hover:text-blush-600">
            Home
          </Link>
          <ChevronRight size={12} strokeWidth={1.5} />
          <Link to="/shop" className="hover:text-blush-600">
            Shop
          </Link>
          <ChevronRight size={12} strokeWidth={1.5} />
          <span className="text-ink-900">{product.name}</span>
        </nav>
      </div>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Gallery */}
        <div className="lg:col-span-7 grid grid-cols-[80px_1fr] md:grid-cols-[100px_1fr] gap-4 md:gap-6">
          <div className="flex flex-col gap-3">
            {product.images.map((src, i) => (
              <button
                key={i}
                onClick={() => setMainIdx(i)}
                data-testid={`product-thumb-${i}`}
                className={`relative aspect-square overflow-hidden rounded-xl border transition ${
                  mainIdx === i ? "border-blush-600" : "border-blush-100 hover:border-blush-300"
                }`}
              >
                <SafeImg src={src} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>

          <div
            className="relative aspect-[4/5] rounded-[2rem] overflow-hidden bg-blush-50 cursor-zoom-in"
            onMouseEnter={() => setZoom(true)}
            onMouseLeave={() => setZoom(false)}
            onMouseMove={handleMouseMove}
            data-testid="product-main-image"
          >
            <SafeImg
              src={product.images[mainIdx]}
              alt={product.name}
              className="h-full w-full object-cover transition-transform duration-300"
              style={{
                transform: zoom ? "scale(1.8)" : "scale(1)",
                transformOrigin: `${mousePos.x}% ${mousePos.y}%`,
              }}
            />
            {product.badge && (
              <span className="absolute top-5 left-5 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-[10px] uppercase tracking-[0.22em] font-body text-blush-600 border border-blush-100">
                {product.badge}
              </span>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="lg:col-span-5 text-center lg:text-left">
          <p className="text-xs uppercase tracking-[0.24em] text-blush-600 font-body">
            {categoryName}
          </p>
          <h1 className="mt-3 font-heading text-4xl md:text-5xl text-ink-900 leading-tight">
            {product.name}
          </h1>

          <div className="mt-4 flex items-center justify-center lg:justify-start gap-2 text-blush-600">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                size={15}
                strokeWidth={1.5}
                fill={i < Math.round(reviewSummary.average || product.rating) ? "currentColor" : "none"}
              />
            ))}
            <span className="text-xs font-body text-ink-500 ml-1">
              {(reviewSummary.average || product.rating).toFixed(1)} · {reviewSummary.count} {reviewSummary.count === 1 ? "review" : "reviews"}
            </span>
          </div>

          <div className="mt-5 flex items-baseline justify-center lg:justify-start gap-3 flex-wrap">
            <span className="font-heading text-3xl text-ink-900">
              {formatINR(product.price)}
            </span>
            {product.originalPrice && (
              <>
                <span className="font-body text-base text-ink-500 line-through">
                  {formatINR(product.originalPrice)}
                </span>
                <span className="font-body text-xs uppercase tracking-[0.2em] text-blush-600 bg-blush-50 px-2 py-1 rounded-full">
                  Save {formatINR(product.originalPrice - product.price)}
                </span>
              </>
            )}
          </div>

          {/* Urgency / social-proof strip */}
          <div className="mt-5 rounded-2xl border border-blush-100 bg-blush-50/50 p-3 space-y-2">
            <div className="flex items-center gap-2 text-xs font-body text-ink-700">
              <Users size={13} strokeWidth={1.7} className="text-blush-600 shrink-0" />
              <span><span className="font-medium text-ink-900">{viewers} people</span> are viewing this right now</span>
            </div>
            {lowStockSize && (
              <div
                className="flex items-center gap-2 text-xs font-body text-white bg-gradient-to-r from-blush-600 to-blush-700 rounded-xl px-3 py-2 animate-pulse-urgent"
                data-testid="urgency-low-stock"
              >
                <Flame size={14} strokeWidth={2} className="shrink-0" />
                <span className="uppercase tracking-wide font-medium">
                  Hurry! Only <span className="font-bold">{lowStockSize.stock} left</span> in size {lowStockSize.size}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 text-xs font-body text-ink-700">
              <Clock size={13} strokeWidth={1.7} className="text-blush-600 shrink-0" />
              <span>Order in the next few hours for <span className="font-medium text-ink-900">dispatch within 24h</span></span>
            </div>
          </div>

          <div className="mt-5" />

          <div className="mt-8">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-[0.22em] text-ink-500 font-body">
                Select size
              </p>
              <button className="text-xs font-body text-blush-600 hover:text-blush-700 underline underline-offset-4" onClick={() => setSizeGuideOpen(true)} type="button" data-testid="size-guide-btn">
                Size guide
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {(allowedSizes ? product.sizes.filter((s) => allowedSizes.includes(s)) : product.sizes).map((s) => {
                const stockLeft = Number(product.stock?.[s] ?? 0);
                const oos = stockLeft <= 0;
                return (
                  <button
                    key={s}
                    onClick={() => !oos && setSize(s)}
                    disabled={oos}
                    data-testid={`detail-size-${s}`}
                    title={oos ? "Out of stock" : ""}
                    className={`h-11 w-11 rounded-full border text-sm font-body transition relative ${
                      oos
                        ? "bg-cream-100 text-ink-500 border-blush-100 line-through cursor-not-allowed"
                        : size === s
                        ? "bg-blush-600 text-white border-blush-600"
                        : "bg-white text-ink-700 border-blush-100 hover:border-blush-300"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-8 flex flex-nowrap items-stretch justify-center lg:justify-start gap-2 sm:gap-3">
            <div className="flex items-center gap-1 px-1 sm:px-2 h-12 rounded-full border border-blush-100 bg-white shrink-0">
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                data-testid="detail-qty-dec"
                className="h-9 w-8 sm:w-9 grid place-items-center rounded-full hover:bg-blush-50 text-ink-700"
              >
                <Minus size={14} strokeWidth={1.5} />
              </button>
              <span
                data-testid="detail-qty"
                className="w-6 sm:w-8 text-center font-body text-ink-900 text-sm"
              >
                {qty}
              </span>
              <button
                onClick={() => setQty((q) => q + 1)}
                data-testid="detail-qty-inc"
                className="h-9 w-8 sm:w-9 grid place-items-center rounded-full hover:bg-blush-50 text-ink-700"
              >
                <Plus size={14} strokeWidth={1.5} />
              </button>
            </div>
            <button
              onClick={handleAdd}
              data-testid="detail-addtocart-btn"
              className="btn-maroon flex-1 min-w-0 px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap overflow-hidden text-ellipsis"
            >
              <span className="hidden sm:inline">Add to cart · {formatINR(product.price * qty)}</span>
              <span className="sm:hidden">Add · {formatINR(product.price * qty)}</span>
            </button>
            <button
              onClick={() => { toggleWishlist(product); toast.success(inWishlist(product.id) ? "Removed from wishlist" : "Added to wishlist"); }}
              data-testid="detail-wishlist-btn"
              className={`h-12 w-12 shrink-0 rounded-full border grid place-items-center transition ${
                inWishlist(product.id)
                  ? "border-blush-600 text-blush-600 bg-blush-50"
                  : "border-blush-100 text-ink-700 hover:border-blush-300"
              }`}
              aria-label="Wishlist"
            >
              <Heart size={16} strokeWidth={1.5} fill={inWishlist(product.id) ? "currentColor" : "none"} />
            </button>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-3">
            {[
              { Icon: Truck, label: "Free shipping", sub: "Pan-India" },
              { Icon: RefreshCw, label: "Easy returns", sub: "Within 7 days" },
              { Icon: Shield, label: "Quality promise", sub: "Hand-finished" },
            ].map((p) => (
              <div
                key={p.label}
                className="rounded-2xl bg-cream-100 border border-blush-100 p-4 text-center"
              >
                <p.Icon size={18} strokeWidth={1.3} className="mx-auto text-blush-600" />
                <p className="mt-2 font-body text-sm text-ink-900">{p.label}</p>
                <p className="font-body text-[11px] text-ink-500">{p.sub}</p>
              </div>
            ))}
          </div>

          {/* Pincode serviceability */}
          <div className="mt-6 rounded-2xl bg-white border border-blush-100 p-4" data-testid="pincode-block">
            <p className="text-xs uppercase tracking-[0.22em] text-ink-500 font-body mb-2">Delivery check</p>
            <form onSubmit={checkPincode} className="flex items-stretch gap-2">
              <input
                type="text" inputMode="numeric" maxLength={6} placeholder="Enter pincode"
                value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))}
                data-testid="pincode-input"
                className="field flex-1"
              />
              <button type="submit" disabled={pinLoading} data-testid="pincode-check-btn" className="btn-ghost-rose text-xs px-5">
                {pinLoading ? "…" : "Check"}
              </button>
            </form>
            {pinResult && pinResult.serviceable && (
              <div className="mt-3 flex items-start justify-center lg:justify-start gap-2 text-sm font-body text-ink-700 text-left" data-testid="pincode-result-ok">
                <Truck size={14} strokeWidth={1.6} className="text-blush-600 mt-0.5 shrink-0" />
                <div>
                  {pinResult.region && (
                    <p className="text-xs text-ink-500 leading-tight mb-0.5">📍 {pinResult.region}</p>
                  )}
                  <p>Deliver by <span className="font-medium text-ink-900">{pinResult.delivery_window}</span></p>
                  <p className="text-xs text-ink-500 mt-0.5">
                    {pinResult.cod_available ? "Cash on delivery available" : "Prepaid only for this pincode"}
                  </p>
                </div>
              </div>
            )}
            {pinResult && !pinResult.serviceable && (
              <p className="mt-3 text-sm font-body text-blush-700" data-testid="pincode-result-no">
                {pinResult.message || `Sorry, we don't deliver to ${pinResult.pincode || pincode} yet.`}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Sticky mobile add-to-cart bar */}
      <div
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-blush-100 px-4 py-3 flex items-center gap-3 shadow-[0_-8px_24px_-12px_rgba(159,51,63,0.2)]"
        data-testid="sticky-addtocart"
      >
        <div className="min-w-0 flex-1">
          <p className="font-heading text-lg text-ink-900 truncate">{formatINR(product.price)}</p>
          <p className="text-[11px] font-body text-ink-500">Size {size} · Qty {qty}</p>
        </div>
        <button
          onClick={handleAdd}
          data-testid="sticky-addtocart-btn"
          className="btn-maroon px-6 text-xs"
        >
          Add to cart
        </button>
      </div>

      {/* Tabs */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-20">
        <Tabs defaultValue="description">
          <TabsList
            className="bg-transparent border-b border-blush-100 w-full justify-start rounded-none h-auto p-0 gap-8"
            data-testid="detail-tabs"
          >
            {[
              { v: "description", l: "Description" },
              ...(fcEnabled ? [{ v: "fabric", l: fc.title || "Fabric & Care" }] : []),
              { v: "reviews", l: `Reviews (${reviewSummary.count || liveReviews.length || reviews.length})` },
            ].map((t) => (
              <TabsTrigger
                key={t.v}
                value={t.v}
                data-testid={`detail-tab-${t.v}`}
                className="rounded-none bg-transparent border-b-2 border-transparent data-[state=active]:border-blush-600 data-[state=active]:bg-transparent data-[state=active]:text-blush-600 data-[state=active]:shadow-none text-ink-700 font-body text-sm uppercase tracking-[0.2em] px-0 pb-4"
              >
                {t.l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="description" className="pt-8">
            <div className="grid md:grid-cols-2 gap-8">
              <p className="font-body text-ink-700 leading-relaxed">
                {product.description} Wear it with flat espadrilles for daytime, or slip on
                dainty heels for softer evenings. Length hits just above the knee — perfect as a
                short kurti or layered over slim trousers.
              </p>
              <ul className="space-y-3 font-body text-ink-700">
                <li>· Hand-finished seams and French hems</li>
                <li>· Slow-made in small batches by our atelier artisans</li>
                <li>· Packaged in recyclable blush kraft with a cotton dust bag</li>
                <li>· True to size. Size up for a relaxed fit.</li>
              </ul>
            </div>
          </TabsContent>

          {fcEnabled && (
            <TabsContent value="fabric" className="pt-8">
              <div className="grid md:grid-cols-2 gap-8 font-body text-ink-700 leading-relaxed">
                <div>
                  <h4 className="font-heading text-xl text-ink-900 mb-3">{fc.fabric_heading || "Fabric"}</h4>
                  <p>{fc.fabric_body || product.fabric}</p>
                </div>
                <div>
                  <h4 className="font-heading text-xl text-ink-900 mb-3">{fc.care_heading || "Care"}</h4>
                  <ul className="space-y-2">
                    {(fc.care_bullets && fc.care_bullets.length ? fc.care_bullets : [
                      "Dry clean recommended for first wash",
                      "Hand wash cold with mild detergent thereafter",
                      "Iron on low, steam from the reverse side",
                      "Store flat-folded in the provided cotton bag",
                    ]).map((b, i) => (
                      <li key={i}>· {b}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </TabsContent>
          )}

          <TabsContent value="reviews" className="pt-8">
            {reviewSummary.count > 0 && (
              <div className="mb-8 flex items-center gap-4 rounded-2xl bg-blush-50/60 border border-blush-100 p-4 max-w-md">
                <div>
                  <p className="font-heading text-4xl text-ink-900">{reviewSummary.average}</p>
                  <p className="text-[11px] font-body uppercase tracking-[0.18em] text-ink-500">out of 5</p>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-1 text-blush-600 mb-1">
                    {[1,2,3,4,5].map((i) => (
                      <Star key={i} size={14} strokeWidth={1.5} fill={i <= Math.round(reviewSummary.average) ? "currentColor" : "none"} />
                    ))}
                  </div>
                  <p className="text-xs font-body text-ink-500">
                    Based on {reviewSummary.count} verified {reviewSummary.count === 1 ? "review" : "reviews"}
                  </p>
                </div>
              </div>
            )}
            <div className="grid md:grid-cols-3 gap-6">
              {(liveReviews.length ? liveReviews : reviews).map((r) => (
                <figure
                  key={r.id}
                  data-testid={`detail-review-${r.id}`}
                  className="rounded-3xl border border-blush-100 bg-white p-6"
                >
                  <div className="flex items-center gap-1 text-blush-600">
                    {Array.from({ length: r.rating }).map((_, i) => (
                      <Star key={i} size={13} strokeWidth={1.5} fill="currentColor" />
                    ))}
                  </div>
                  {r.title && <h5 className="mt-3 font-heading text-lg text-ink-900">{r.title}</h5>}
                  <p className="mt-2 font-body text-sm text-ink-700 leading-relaxed">{r.body}</p>
                  <figcaption className="mt-5 text-xs font-body text-ink-500">
                    — {r.name}{r.verified && <span className="ml-2 text-blush-600">✓ Verified</span>}{r.date && ` · ${r.date}`}
                  </figcaption>
                </figure>
              ))}
              {!liveReviews.length && !reviews.length && (
                <p className="md:col-span-3 text-center font-body text-ink-500 py-8">
                  No reviews yet — be the first!
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </section>

      {/* Related */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24">
        <div className="text-center">
          <span className="font-script text-3xl text-blush-400">you may also love</span>
          <h2 className="font-heading text-3xl md:text-4xl text-ink-900 mt-1">
            Related pieces
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {related.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      <SizeGuideDialog open={sizeGuideOpen} onOpenChange={setSizeGuideOpen} />
    </div>
  );
}
